import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useUser } from "@clerk/clerk-react";
import {
  Zap, BookOpen, Gem, Server, Skull, CheckCircle,
  Cpu, Sword, LayoutDashboard, Gamepad2, ListTodo,
  Monitor, HardDrive, CircuitBoard, Fan, HelpCircle, Clock,
  Siren, ShieldCheck, Star, Play, Target, User
} from 'lucide-react';
// Components
import SlotReels from './SlotReels';
import ControlPanel from './ControlPanel';
import DataMines from './DataMines';
import HardwareShop from './HardwareShop';
import GemShop from './GemShop';
import NumberTicker from './NumberTicker';
import '../styles/CasinoTheme.css';

// Context & Data
import { useGame } from '../context/GameContext';
import { LEARNING_DATA } from '../data/learningData';
import { MISSIONS } from '../data/missions';
import { QUESTION_DB } from '../data/questions';
import { CP_TABLE } from '../data/rules';

// --- THEME CONFIG ---
const DB_THEMES = {
  sqlite: { name: 'SQLite', color: 'text-emerald-400', glow: 'shadow-emerald-500/20', border: 'border-emerald-500/20', multiplier: 1 },
  postgres: { name: 'PostgreSQL', color: 'text-blue-400', glow: 'shadow-blue-500/20', border: 'border-blue-500/20', multiplier: 1.5 },
  oracle: { name: 'Oracle', color: 'text-red-500', glow: 'shadow-red-500/20', border: 'border-red-500/20', multiplier: 2.5 }
};

const BOSS_DATA = [
  { level: 5, name: "The Infinite Loop", hp: 1000, reward: 5000 },
  { level: 10, name: "Little Bobby Tables", hp: 2500, reward: 15000 },
  { level: 15, name: "Deadlock Demon", hp: 5000, reward: 20000 },
  { level: 20, name: "Cartesian Explosion", hp: 8000, reward: 25000 },
  { level: 50, name: "The Monolith", hp: 50000, reward: 50000 },
  { level: 100, name: "The Singularity", hp: 1000000, reward: 100000 }
];

// --- TUTORIAL TOOLKIT ---
const TUTORIAL_STEPS = [
  { target: 'stats-panel', title: 'OPERATOR STATS', text: '• Level: Determined by your CP. Unlocks new Bosses.\n• CP: Career Points (XP). Earned via spins & missions.\n• System Specs: Real-time view of your active hardware bonuses.', position: 'right' },
  { target: 'rig-btn', title: 'RIG THE CASINO', text: 'Customize your rig to cheat the system:\n• CPU: Multiplies Cash Winnings\n• RAM: Discounts on Shop Items & Lifelines\n• MOBO: Boosts CP (Leveling Speed)\n• PSU: Chance to Refund Lost Bets\n• GPU: Luck (Increases Jackpot & Critical Hits)\n• FAN: Protection (Blocks Glitches & Security Checks)', position: 'bottom' },
  { target: 'jackpot-panel', title: 'THE JACKPOT', text: 'The Ultimate Prize.\n• Grows with every spin (1% of bets added).\n• HOW TO WIN: You must defeat "The Singularity" (Lvl 100 Boss).\n• Winner takes it all.', position: 'bottom' },
  { target: 'slot-machine', title: 'QUERY ENGINE', text: 'Generates SQL Queries via slot mechanics.\n• Valid Syntax: Triggers a Bonus Question.\n• Evaluation: Answer correctly to execute the query and win cash.\n• Glitches: Random events that require immediate fixes.', position: 'bottom' },
  { target: 'control-panel', title: 'EXECUTION TERMINAL', text: 'Risk Management Console.\n• Wager: Control your bet size.\n• Difficulty: Increases Question complexity.\n• REWARD: Higher difficulty = Massive CP Multipliers (up to 5x).', position: 'top' },
  { target: 'quest-panel', title: 'BOUNTIES & FLASHCARDS', text: 'Side Operations.\n• Dailies: Rotating tasks for Gems (Premium Currency).\n• Flashcards: Flip to learn terms & earn quick, risk-free cash.\n• Missions: Long-term milestones with huge payouts.', position: 'left' }
];

const TutorialOverlay = ({ onClose }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    const updateRect = () => {
      const step = TUTORIAL_STEPS[stepIndex];
      const el = document.getElementById(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    updateRect();
    const timer = setTimeout(updateRect, 50);
    window.addEventListener('resize', updateRect);
    return () => { window.removeEventListener('resize', updateRect); clearTimeout(timer); };
  }, [stepIndex]);

  if (!rect) return null;
  const step = TUTORIAL_STEPS[stepIndex];
  const isLast = stepIndex === TUTORIAL_STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      <div className="absolute transition-all duration-300 ease-in-out border-2 border-fuchsia-500 rounded-xl"
        style={{ top: rect.top - 5, left: rect.left - 5, width: rect.width + 10, height: rect.height + 10, boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.85)' }}
      >
        <div className="absolute inset-0 shadow-[0_0_50px_rgba(236,72,153,0.5)] rounded-xl pointer-events-none" />
      </div>
      <motion.div key={stepIndex} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="absolute max-w-xs bg-slate-900 border border-cyan-500/50 p-6 rounded-2xl shadow-2xl z-[101]"
        style={{ top: rect.top + rect.height + 20 > window.innerHeight - 200 ? rect.top - 180 : rect.top + rect.height + 20, left: Math.max(20, Math.min(window.innerWidth - 340, rect.left)) }}
      >
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-cyan-400 font-black uppercase tracking-widest text-sm">{step.title}</h3>
          <span className="text-[10px] font-mono text-slate-500">{stepIndex + 1} / {TUTORIAL_STEPS.length}</span>
        </div>
        <p className="text-slate-300 text-xs leading-relaxed mb-4">{step.text}</p>
        <div className="flex justify-between gap-3">
          <button onClick={onClose} className="text-[10px] font-bold text-slate-500 hover:text-white uppercase">Skip Protocol</button>
          <button onClick={() => isLast ? onClose() : setStepIndex(s => s + 1)} className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-xs font-black px-4 py-2 rounded-lg shadow-lg">
            {isLast ? 'INITIALIZE SYSTEM' : 'NEXT MODULE'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default function GameContainer() {
  const navigate = useNavigate();
  const { user } = useUser();
  const {
    balance, setBalance, cp, addCp, gems, setGems,
    inventory, equipped, setEquipped,
    flippedCards, setFlippedCards,
    activeStats, activeChallenge,
    isMuted,
    defeatedBosses, markBossDefeated,
    completedMissions, claimedMissions, claimMissionReward,
    dailyStatus, claimDailyReward,
    incrementSpins, incrementQuestionsAnswered, checkMissions,
    purchaseItem,
    checkDailyChallenge, trackInteraction, trackLifelineUse
  } = useGame();

  const ambienceRef = useRef(null);
  const bossAudioRef = useRef(null);

  // --- STATE ---
  const [betAmount, setBetAmount] = useState(100);
  const [difficulty, setDifficulty] = useState('Easy');
  const [isSpinning, setIsSpinning] = useState(false);
  const [showQuestion, setShowQuestion] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [gameMessage, setGameMessage] = useState("SYSTEM READY // PLACE BETS");
  const [showConfetti, setShowConfetti] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  // --- NEW: TIMER STATE ---
  const QUESTION_DURATION = 60; // Seconds allowed
  const [timer, setTimer] = useState(QUESTION_DURATION);

  // --- CHEAT DETECTION STATE ---
  const [hasCheated, setHasCheated] = useState(false);

  // Modals
  const [showHardware, setShowHardware] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [inDataMine, setInDataMine] = useState(false);

  // Gameplay
  const [activeCategory, setActiveCategory] = useState('SQL');
  const [activeEvent, setActiveEvent] = useState(null);
  const [bossState, setBossState] = useState({ active: false, currentHp: 0, maxHp: 0, name: '', level: 0 });
  const [bossModeEngaged, setBossModeEngaged] = useState(false);

  const [jackpot, setJackpot] = useState(5000000);
  const [prestigeLevel] = useState('sqlite');

  // Reels & Slots
  const [currentReels, setCurrentReels] = useState(['SELECT', '*', 'FROM Orders', 'WHERE id > 0', 'LIMIT 10']);
  const [heldReels, setHeldReels] = useState([false, false, false, false, false]);

  // Quiz
  const [selectedOption, setSelectedOption] = useState(null);
  const [seenQuestions, setSeenQuestions] = useState([]);

  const [disabledOptions, setDisabledOptions] = useState([]);
  const [lifelinesActive, setLifelinesActive] = useState({ hint: false, debug: false });

  // Layout & UX
  const [isShaking, setIsShaking] = useState(false);
  const [mobileTab, setMobileTab] = useState('GAME');

  const THEME = DB_THEMES[prestigeLevel] || DB_THEMES['sqlite'];
  const MULTIPLIERS = { Easy: 1.5, Medium: 3, Hard: 5 };
  const LIFELINE_PERCENTS = { fifty: 0.20, hint: 0.30, debug: 0.80 };

  // --- CHEAT DETECTION LOGIC (CONDITIONAL) ---
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only trigger if tab hidden AND Question is Active AND Not answered yet
      if (document.visibilityState === 'hidden' && showQuestion && !selectedOption) {
        if (!hasCheated) { // Prevent double deduction if triggers multiple times quickly
          setHasCheated(true);
          setBalance(prev => Math.max(0, prev - betAmount)); // Penalty: Lose bet
          playSound('error-buzz');
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [betAmount, setBalance, hasCheated, showQuestion, selectedOption]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- CHEAT DISMISS HANDLER (RESETS ROUND) ---
  const handleCheatDismiss = () => {
    setHasCheated(false);
    // Completely Reset the Question State
    setShowQuestion(false);
    setCurrentQuestion(null);
    setSelectedOption(null);

    setDisabledOptions([]);
    setLifelinesActive({ hint: false, debug: false });

    // Reset Timer & Status
    setTimer(QUESTION_DURATION);
    setGameMessage("PENALTY APPLIED // SPIN AGAIN");
    playSound('click');
  };

  // --- TIMER EFFECT ---
  useEffect(() => {
    let interval;
    if (showQuestion && !selectedOption && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (showQuestion && !selectedOption && timer === 0) {
      handleTimeout();
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQuestion, timer, selectedOption]);

  const handleTimeout = () => {
    if (selectedOption) return;
    playSound('error-buzz');

    triggerShake();
    setGameMessage("CONNECTION TIMED OUT // QUERY FAILED");
    setSelectedOption('TIMEOUT'); // Lock options

    // Treat as incorrect answer for streaks
    incrementQuestionsAnswered(false);

    setTimeout(() => {
      setShowQuestion(false);
      setCurrentQuestion(null);
      setSelectedOption(null);

    }, 2500);
  };

  // --- AUTO START TUTORIAL ---
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial_v1');
    if (!hasSeenTutorial) {
      setTimeout(() => {
        setShowTutorial(true);
        localStorage.setItem('hasSeenTutorial_v1', 'true');
      }, 1000);
    }
  }, []);

  // --- AUDIO HELPER ---
  const playSound = (type) => {
    if (isMuted) return;
    try {
      const audio = new Audio(`/sounds/${type}.mp3`);
      audio.volume = 0.5;
      audio.play().catch(() => { });
    } catch (e) { }
  };

  // --- BOSS MUSIC CONTROL ---
  const playBossMusic = () => {
    if (bossAudioRef.current) return;
    try {
      const audio = new Audio('/sounds/boss-spawn.mp3');
      audio.loop = true;
      audio.volume = 0.6;
      bossAudioRef.current = audio;
      if (!isMuted) audio.play().catch(() => { });
    } catch (e) { }
  };

  const stopBossMusic = () => {
    if (bossAudioRef.current) {
      bossAudioRef.current.pause();
      bossAudioRef.current.currentTime = 0;
      bossAudioRef.current = null;
    }
  };

  const triggerShake = () => {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
  };

  // Ambience
  useEffect(() => {
    const audio = new Audio('/sounds/ambience.mp3');
    audio.loop = true;
    audio.volume = 0.3;
    ambienceRef.current = audio;
    if (!isMuted) audio.play().catch(() => { });

    return () => {
      if (ambienceRef.current) ambienceRef.current.pause();
      stopBossMusic();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMuted) {
      if (ambienceRef.current) ambienceRef.current.pause();
      if (bossAudioRef.current) bossAudioRef.current.pause();
    } else {
      if (ambienceRef.current) ambienceRef.current.play().catch(() => { });
      if (bossAudioRef.current) bossAudioRef.current.play().catch(() => { });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMuted]);

  // --- BANKRUPTCY ---
  useEffect(() => {
    if (balance < 10 && !isSpinning && !showQuestion && !activeEvent) {
      setGameMessage("BANKRUPT DETECTED! REDIRECTING...");
      checkDailyChallenge('BANKRUPT', null);
      const timer = setTimeout(() => { navigate('/flipcards'); }, 2000);
      return () => clearTimeout(timer);
    }
  }, [balance, isSpinning, showQuestion, activeEvent, navigate, checkDailyChallenge]);

  // --- BOSS TRIGGER ---
  const triggerBoss = (bossData) => {
    setActiveEvent('boss');
    setBossModeEngaged(false);
    setBossState({ active: true, currentHp: bossData.hp, maxHp: bossData.hp, name: bossData.name, level: bossData.level, reward: bossData.reward });
    setGameMessage(`WARNING: ${bossData.name} DETECTED!`);
    playBossMusic();
    triggerShake();
  };

  useEffect(() => {
    const currentLevel = Math.floor(cp / 100) + 1;
    const pendingBoss = BOSS_DATA.find(b => currentLevel >= b.level && !defeatedBosses.includes(b.level));

    // Only trigger if no active event AND the boss hasn't been engaged yet in this session
    if (pendingBoss && !activeEvent && !bossState.active) {
      console.log("BOSS TRIGGERED:", pendingBoss.name);
      triggerBoss(pendingBoss);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cp, activeEvent, defeatedBosses, bossState.active]);

  // --- ACTIONS ---
  const openGemShop = () => {
    setShowShop(true);
    checkDailyChallenge('NAVIGATE', 'GEM_SHOP');
  };

  const handleBuyHardware = (item, price) => {
    // Use the explicitly passed price (discounted), fallback to item.cost
    const finalCost = price !== undefined ? price : item.cost;

    const success = purchaseItem(item.id, finalCost);
    if (success) {
      playSound('win');
      setGameMessage(`INSTALLED: ${item.name}`);
      setShowHardware(false);
      setTimeout(() => setShowHardware(true), 10);
    } else {
      playSound('lose');
      setGameMessage("INSUFFICIENT FUNDS");
      triggerShake();
    }
  };

  const handleEquipHardware = (item) => {
    setEquipped(prev => ({ ...prev, [item.type]: item.id }));
    playSound('click');
    checkDailyChallenge('EQUIP', null);
  };

  const handleDailyClaim = () => {
    const success = claimDailyReward();
    if (success) {
      playSound('win');
      setGameMessage("DAILY REWARD CLAIMED!");
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2000);
    }
  };

  const handleMissionClaim = (id) => {
    const success = claimMissionReward(id);
    if (success) {
      playSound('win');
      setGameMessage("BOUNTY CLAIMED!");
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2000);
    }
  };

  const getLifelineCost = (type) => {
    const baseCost = Math.floor(balance * LIFELINE_PERCENTS[type]);
    const discountMult = 1 - (activeStats?.discount || 0);
    return Math.floor(baseCost * discountMult);
  };

  const parseQueryToReels = (query) => {
    let parts = ['', '', '', '', ''];
    const q = query.replace(/\s+/g, ' ').trim();
    const actionMatch = q.match(/^(\w+)/);
    parts[0] = actionMatch ? actionMatch[1].toUpperCase() : 'SELECT';
    const fromIndex = q.toUpperCase().indexOf(' FROM ');
    if (fromIndex !== -1) {
      parts[1] = q.substring(parts[0].length, fromIndex).trim();
      let remainder = q.substring(fromIndex + 6);
      const tableMatch = remainder.match(/^([^\s]+)/);
      const tableName = tableMatch ? tableMatch[1] : '';
      parts[2] = `FROM ${tableName}`;
      remainder = remainder.substring(tableName.length).trim();
      const modIndex = remainder.search(/\b(ORDER BY|GROUP BY|LIMIT|HAVING)\b/i);
      if (modIndex !== -1) {
        parts[4] = remainder.substring(modIndex).trim();
        parts[3] = remainder.substring(0, modIndex).trim();
      } else {
        parts[3] = remainder;
        parts[4] = ';';
      }
    } else {
      parts[1] = q.substring(parts[0].length).trim();
      parts[2] = ''; parts[3] = ''; parts[4] = ';';
    }
    return parts.map(p => p === '' ? '[ - ]' : p);
  };

  // --- SPIN ---
  const handleSpin = () => {
    const holdCost = heldReels.filter(h => h).length * (betAmount * 0.5);
    const totalCost = betAmount + holdCost;

    if (balance < 10) { setGameMessage("BANKRUPT! REDIRECTING..."); return; }
    if (balance < totalCost) {
      setGameMessage("INSUFFICIENT FUNDS");
      playSound('lose');
      triggerShake();
      return;
    }

    incrementSpins(betAmount);

    // Active Heatmap Trigger (Spin)
    if (user) {
      axios.post('https://server.datasenseai.com/user-streak/update-activity', { clerkId: user.id })
        .catch(err => console.error("Activity update failed", err));
    }

    const filteredQuestions = QUESTION_DB.filter(q => q.difficulty === difficulty);

    // Filter out seen questions
    let availableQuestions = filteredQuestions.filter(q => !seenQuestions.includes(q.id));

    // If all questions seen, reset seen list for this difficulty (or just pick from full pool and clear seen)
    if (availableQuestions.length === 0) {
      availableQuestions = filteredQuestions;
      // We might want to clear seenQuestions, but specifically for this difficulty?
      // For simplicity, we'll allow repeats now but cleaning the slate is complex if we mix difficulties.
      // Better: Keep seenQuestions but remove THESE ids from it? Too complex.
      // Simple Reset:
      setSeenQuestions([]);
    }

    const nextQuestion = availableQuestions[Math.floor(Math.random() * availableQuestions.length)];

    // Mark as seen
    setSeenQuestions(prev => [...prev, nextQuestion.id]);

    const targetReels = parseQueryToReels(nextQuestion.query);

    const luckBonus = activeStats?.luck || 0;
    setJackpot(prev => prev + (betAmount * 0.01 * (1 + luckBonus)));
    setShowQuestion(false);
    setSelectedOption(null);

    setDisabledOptions([]);
    setLifelinesActive({ hint: false, debug: false });

    const protection = activeStats?.protection || 0;
    const injectionChance = 0.01 * (1 - protection);
    if (Math.random() < injectionChance) { triggerInjection(); return; }

    playSound('spin-loop');
    setBalance(prev => prev - totalCost);
    setIsSpinning(true);
    setGameMessage("EXECUTING QUERY...");

    setTimeout(() => {
      setIsSpinning(false);
      setCurrentReels(targetReels);
      setHeldReels([false, false, false, false, false]);

      checkMissions('QUERY', nextQuestion.query.toUpperCase());

      const protection = activeStats?.protection || 0;
      const glitchChance = 0.05 * (1 - protection);

      if (Math.random() < glitchChance) {
        triggerGlitch(nextQuestion.query);
      } else {
        setCurrentQuestion(nextQuestion);
        setShowQuestion(true);
        setTimer(QUESTION_DURATION); // --- RESET TIMER ON NEW QUESTION ---
        setGameMessage("VALIDATION REQUIRED // ANSWER TO CLAIM");
      }
    }, 2000);
  };

  // --- ANSWER LOGIC ---
  const handleOptionClick = (option) => {
    if (selectedOption) return;
    setSelectedOption(option);

    const isCorrect = option === currentQuestion.answer;
    incrementQuestionsAnswered(isCorrect);

    if (isCorrect) {
      playSound('win-small');


      const cpuBonus = activeStats?.multiplier || 1.0;
      const prestigeMult = THEME.multiplier || 1.0;
      const difficultyMult = MULTIPLIERS[difficulty] || 2;
      let baseWin = Math.floor(betAmount * difficultyMult);

      // --- LUCK BONUS (GPU) ---
      const luck = activeStats?.luck || 0;
      let isLucky = false;
      if (Math.random() < luck) {
        baseWin = Math.floor(baseWin * 1.5);
        isLucky = true;
      }

      const totalWin = Math.floor(baseWin * cpuBonus * prestigeMult);

      // Determine base CP based on difficulty using CP_TABLE
      let baseCpReward = CP_TABLE.SPIN_WIN_EASY;
      if (difficulty === 'Medium') baseCpReward = CP_TABLE.SPIN_WIN_MEDIUM;
      if (difficulty === 'Hard') baseCpReward = CP_TABLE.SPIN_WIN_HARD;

      const xpGain = Math.floor(baseCpReward * cpuBonus);

      if (bossState.active) {
        let damage = difficulty === 'Hard' ? 300 : 100;

        // --- BOSS CRIT (LUCK) ---
        const luck = activeStats?.luck || 0;
        let isCrit = false;
        if (Math.random() < luck) {
          damage = Math.floor(damage * 1.5);
          isCrit = true;
        }

        const newHp = bossState.currentHp - damage;
        setBossState(prev => ({ ...prev, currentHp: newHp }));
        triggerShake();
        setGameMessage(isCrit ? `LUCKY CRIT! -${damage} HP` : `DIRECT HIT! -${damage} HP`);
        playSound('boss-hit');

        checkDailyChallenge('BOSS_DAMAGE', { damage });

        if (newHp <= 0) {
          stopBossMusic();
          const bossRewardXP = 150;
          const cashReward = bossState.reward || 5000;
          setBalance(prev => prev + cashReward);
          addCp(bossRewardXP);
          setGems(prev => prev + 5);

          markBossDefeated(bossState.level, bossState.name);

          setBossState({ active: false, currentHp: 0, level: 0 });
          setActiveEvent(null);
          setBossModeEngaged(false);
          setGameMessage(`BOSS DEFEATED! +$${cashReward.toLocaleString()} | +${bossRewardXP} CP`);
          setShowConfetti(true);
          playSound('jackpot');
        }
      } else {
        setBalance(prev => prev + totalWin);
        addCp(xpGain);
        setGameMessage(isLucky ? `LUCKY HIT! +$${totalWin.toLocaleString()}` : `SUCCESS! +$${totalWin.toLocaleString()}`);
        setShowConfetti(true);
        checkMissions('WIN', totalWin);
        checkDailyChallenge('WIN', { amount: totalWin });

        // Active Heatmap Trigger (Win)
        if (user) {
          axios.post('https://server.datasenseai.com/user-streak/update-activity', { clerkId: user.id })
            .catch(err => console.error("Activity update failed", err));
        }
      }
    } else {
      playSound('error-buzz');

      triggerShake();
      const refundChance = activeStats?.refundChance || 0;
      if (Math.random() < refundChance && !bossState.active) {
        setBalance(prev => prev + betAmount);
        setGameMessage("SURGE PROTECTOR SAVED BET!");
      } else {
        setGameMessage("QUERY FAILED. TRY AGAIN.");
      }
    }

    setTimeout(() => {
      setShowConfetti(false);
      setShowQuestion(false);
      setCurrentQuestion(null);
      setSelectedOption(null);
    }, 2500);
  };

  // --- EVENTS ---
  const activateFiftyFifty = () => {
    const cost = getLifelineCost('fifty');
    if (balance >= cost && disabledOptions.length === 0) {
      setBalance(prev => prev - cost);
      const wrongOptions = currentQuestion.options.filter(o => o !== currentQuestion.answer);
      setDisabledOptions(wrongOptions.sort(() => 0.5 - Math.random()).slice(0, 2));
      playSound('click');
      trackLifelineUse('fifty');
    }
  };

  const activateHint = () => {
    const cost = getLifelineCost('hint');
    if (balance >= cost && !lifelinesActive.hint) {
      setBalance(prev => prev - cost);
      setLifelinesActive(prev => ({ ...prev, hint: true }));
      playSound('click');
      trackLifelineUse('hint');
    }
  };

  const activateDebug = () => {
    const cost = getLifelineCost('debug');
    if (balance >= cost && !lifelinesActive.debug) {
      setBalance(prev => prev - cost);
      setLifelinesActive(prev => ({ ...prev, debug: true }));
      playSound('click');
      trackLifelineUse('debug');
    }
  };

  const toggleHold = (index) => {
    if (!isSpinning) {
      const newHeld = [...heldReels];
      newHeld[index] = !newHeld[index];
      setHeldReels(newHeld);
      playSound('click');
      trackInteraction('hold_reel');
    }
  };

  const triggerInjection = () => { setActiveEvent('injection'); setGameMessage("SECURITY BREACH DETECTED"); playSound('glitch-noise'); triggerShake(); };
  const triggerGlitch = (query) => {

    setActiveEvent('glitch');
    setGameMessage("MALFUNCTION: REPAIR REQUIRED");
    triggerShake();
    playSound('glitch-noise');
  };

  const handleFlip = (id) => {
    if (flippedCards.length >= 4) {
      setGameMessage("DAILY LIMIT (4/4) REACHED");
      playSound('lose');
      return;
    }
    if (!flippedCards.includes(id)) {
      setFlippedCards(id, activeCategory);
      setBalance(prev => prev + 100);
      setGameMessage("CARD DECRYPTED: +$100");
      addCp(15);
      playSound('click');
    }
  };

  const safeBalance = balance || 0;
  const safeCp = cp || 0;
  const safeGems = gems || 0;
  const safeLevel = Math.floor(safeCp / 100) + 1;

  if (inDataMine) return <DataMines onExitMine={(earnings) => { setBalance(prev => prev + earnings); setInDataMine(false); }} />;
  const currentMiniCards = LEARNING_DATA[activeCategory] || [];

  const multVal = activeStats?.multiplier || 1;
  const luckVal = Math.floor((activeStats?.refundChance || 0) * 100);
  const discVal = Math.floor((activeStats?.discount || 0) * 100);

  const HARDWARE_SLOTS = [
    { type: 'cpu', label: 'CPU', icon: Cpu, color: 'text-cyan-400' },
    { type: 'gpu', label: 'GPU', icon: Monitor, color: 'text-fuchsia-400' },
    { type: 'ram', label: 'RAM', icon: HardDrive, color: 'text-emerald-400' },
    { type: 'psu', label: 'PSU', icon: Zap, color: 'text-yellow-400' },
    { type: 'mobo', label: 'MOBO', icon: CircuitBoard, color: 'text-indigo-400' },
    { type: 'fan', label: 'FAN', icon: Fan, color: 'text-cyan-400' },
  ];

  return (
    <div onClick={() => trackInteraction('bg_click')} className="w-full h-screen font-sans text-slate-50 overflow-hidden relative selection:bg-fuchsia-400/30 selection:text-yellow-100 safe-area app-grid-bg app-glow pb-0">

      {/* --- CHEAT DETECTION OVERLAY --- */}
      {hasCheated && (
        <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center text-center p-6 animate-in fade-in duration-300">
          <div className="bg-red-500/10 p-8 rounded-3xl border border-red-500/50 shadow-[0_0_50px_rgba(239,68,68,0.3)] max-w-lg w-full">
            <Siren className="w-20 h-20 text-red-500 mx-auto mb-6 animate-pulse" />
            <h2 className="text-4xl font-black text-white mb-2 uppercase tracking-widest">Busted!</h2>
            <div className="text-red-400 font-mono text-sm mb-6 uppercase tracking-[0.2em]">Tab Switching Detected</div>
            <p className="text-white/70 mb-8 leading-relaxed">
              Anti-cheat protocols engaged. You have been penalized for leaving the terminal.
              <br /><br />
              <span className="text-red-400 text-xl font-black">-{betAmount} CHIPS</span>
            </p>
            <button
              onClick={handleCheatDismiss}
              className="w-full py-4 rounded-xl bg-red-600 hover:bg-red-500 text-white font-black uppercase tracking-widest transition-all hover:scale-[1.02] shadow-lg"
            >
              I Promise To Focus
            </button>
          </div>
        </div>
      )}

      {/* --- NEON BORDER STYLES --- */}
      <style>
        {`
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .neon-gradient-border {
            position: relative;
            background: linear-gradient(#020617, #020617) padding-box,
                        linear-gradient(60deg, #ec4899, #8b5cf6, #3b82f6, #10b981, #eab308, #ec4899) border-box;
            border: 2px solid transparent;
            background-size: 300% 300%;
            animation: gradientMove 4s ease infinite;
            box-shadow: 0 0 15px rgba(139, 92, 246, 0.15);
          }
          .neon-gradient-border:hover {
             box-shadow: 0 0 25px rgba(139, 92, 246, 0.3);
          }
        `}
      </style>

      {/* VEGAS CASINO FLOOR BACKGROUND */}
      <div className="fixed inset-0 -z-10 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_800px_at_30%_20%,rgba(236,72,153,0.32),transparent_60%),radial-gradient(900px_700px_at_75%_25%,rgba(34,211,238,0.32),transparent_55%),radial-gradient(900px_700px_at_55%_80%,rgba(250,204,21,0.22),transparent_60%),linear-gradient(180deg,#1a0020_0%,#070018_42%,#04020a_100%)]" />
        <div
          className="absolute inset-0 opacity-[0.20] mix-blend-overlay"
          style={{
            backgroundImage:
              'repeating-linear-gradient(135deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 9px), repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 11px)'
          }}
        />
        <motion.div
          className="absolute -top-44 left-1/2 h-[920px] w-[920px] -translate-x-1/2 rounded-full blur-3xl opacity-35"
          animate={{ rotate: 360 }}
          transition={{ duration: 44, repeat: Infinity, ease: "linear" }}
          style={{
            background:
              "conic-gradient(from 90deg, rgba(250,204,21,0.0), rgba(250,204,21,0.30), rgba(236,72,153,0.22), rgba(34,211,238,0.22), rgba(250,204,21,0.0))"
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_45%,rgba(0,0,0,0.45)_100%)]" />
      </div>

      {showConfetti && <Confetti numberOfPieces={450} recycle={false} />}

      {/* TOP HUD */}
      <div className="sticky top-0 z-60 w-full border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto max-w-[650px] px-3 md:px-6 py-3 flex items-center justify-between gap-4 overflow-x-auto scrollbar-hide">

          <div className="flex items-center gap-3 shrink-0">

            <div id="jackpot-panel" className="relative rounded-xl neon-gradient-border px-4 py-1.5 overflow-hidden min-w-[140px] text-center">
              <div className="text-[9px] font-black text-yellow-500 tracking-[0.2em] uppercase mb-0.5">MEGA JACKPOT</div>
              <div className="text-lg font-black text-white font-mono leading-none">
                <NumberTicker value={jackpot} prefix="$" />
              </div>
            </div>

            <div className="rounded-xl neon-gradient-border px-3 py-1.5 min-w-[100px]">
              <div className="text-[9px] font-black text-cyan-400 tracking-widest uppercase">CHIPS</div>
              <div className="text-base font-black text-white font-mono leading-none">
                <NumberTicker value={safeBalance} prefix="$" />
              </div>
            </div>

            <div className="rounded-xl neon-gradient-border px-3 py-1.5 min-w-[90px] cursor-pointer" onClick={openGemShop}>
              <div className="text-[9px] font-black text-fuchsia-400 tracking-widest uppercase">GEMS</div>
              <div className="text-base font-black text-white font-mono leading-none flex items-center gap-1">
                <Gem size={12} /> <NumberTicker value={safeGems} />
              </div>
            </div>

            <button id="rig-btn" onClick={() => setShowHardware(true)} className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-4 py-2 rounded-xl font-black text-xs shadow-lg flex items-center gap-2 transition-all active:scale-95 shrink-0 border border-cyan-400/50">
              <Server size={16} /> <span className="hidden md:inline">CASINO RIG TOOLS</span>
            </button>

            <button onClick={() => setShowTutorial(true)} className="bg-slate-800 text-slate-400 hover:text-white p-2 rounded-xl border border-white/10 shrink-0">
              <HelpCircle size={18} />
            </button>

            {/* <button
              onClick={() => { setBalance(prev => prev + 5000); playSound('win'); }}
              className="bg-red-900/50 hover:bg-red-600 text-red-200 hover:text-white px-2 py-1 rounded-lg text-[10px] font-bold border border-red-500/30 uppercase"
              title="Add Test Funds"
            >
              +5K
            </button> */}

          </div>
        </div>
      </div>

      {/* MAIN CONTENT GRID */}
      <div className=" max-w-[1600px] px-3 md:px-6 py-4 md:py-6 h-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full">

          {/* LEFT COLUMN: STATS */}
          <div id="stats-panel" className={`lg:col-span-3 flex-col gap-5 lg:h-full lg:overflow-y-auto scrollbar-hide ${mobileTab === 'STATS' ? 'flex' : 'hidden lg:flex'}`}>

            <div className="neon-gradient-border rounded-3xl p-4 shadow-xl shrink-0">
              <div className="flex justify-between items-start mb-1">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">Operator Rank</div>
                <div className="flex flex-col items-end">
                  <div className="text-[9px] font-bold text-slate-500 font-mono tracking-wider mb-0.5">TOTAL CP: {safeCp}</div>
                  <div className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">EMERALD</div>
                </div>
              </div>
              <div className="flex justify-between items-end mb-2">
                <div className="text-3xl font-black text-white">{safeLevel}</div>
                <div className="text-xs font-mono text-slate-400 mb-1">{safeCp % 100} / 100 CP</div>
              </div>
              <div className="w-full bg-black/50 h-2 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400" style={{ width: `${safeCp % 100}%` }}></div>
              </div>
            </div>

            <div className="neon-gradient-border rounded-3xl p-4 shadow-lg shrink-0">
              <div className="flex items-center gap-3 mb-2 border-b border-cyan-500/20 pb-2">
                <Cpu size={16} className="text-cyan-400" />
                <h3 className="text-xs font-black text-white uppercase tracking-widest">System Specs</h3>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Multiplier</span>
                  <span className="text-xs font-mono font-black text-yellow-400">{multVal}x</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Luck</span>
                  <span className="text-xs font-mono font-black text-fuchsia-400">+{luckVal}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Discount</span>
                  <span className="text-xs font-mono font-black text-emerald-400">-{discVal}%</span>
                </div>
              </div>
            </div>

            <div className="neon-gradient-border rounded-3xl p-4 shrink-0">
              <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Active Hardware</h3>
              <div className="grid grid-cols-4 gap-2">
                {HARDWARE_SLOTS.map((slot, idx) => {
                  const isActive = equipped[slot.type];
                  const IconComponent = slot.icon;
                  return (
                    <div key={idx} className={`aspect-square rounded-xl border flex flex-col items-center justify-center transition-all ${isActive ? 'bg-white/10 border-white/20 shadow-lg shadow-white/5' : 'bg-black/20 border-white/5'}`}>
                      <IconComponent size={20} className={isActive ? slot.color : 'text-slate-600'} />
                      <span className="text-[8px] font-bold mt-1 text-slate-400 uppercase">{slot.label}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* CENTER COLUMN: THE MACHINE */}
          <motion.div
            animate={isShaking ? { x: [-5, 5, -5, 5, 0] } : {}}
            transition={{ duration: 0.4 }}
            className={`lg:col-span-6 flex flex-col h-full lg:overflow-y-auto scrollbar-hide pb-20 lg:pb-0 ${mobileTab === 'GAME' ? 'flex' : 'hidden lg:flex'}`}
          >
            <div className={`w-full mb-3 rounded-2xl border px-4 py-2 shadow-lg relative overflow-hidden transition-all duration-500
                ${bossState.active && bossModeEngaged
                ? 'bg-red-950/80 border-red-500/50'
                : 'bg-slate-900/80 neon-gradient-border'}`
            }>
              {bossState.active && bossModeEngaged ? (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-red-400 font-black uppercase tracking-widest text-xs">{bossState.name}</span>
                  <div className="flex-1 h-2 bg-black rounded-full overflow-hidden border border-red-500/30">
                    <motion.div className="h-full bg-red-600" initial={{ width: '100%' }} animate={{ width: `${(bossState.currentHp / bossState.maxHp) * 100}%` }} />
                  </div>
                  <span className="text-red-400 font-mono font-bold text-xs">{bossState.currentHp} HP</span>
                </div>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-yellow-400 font-black uppercase tracking-widest text-xs animate-pulse">{gameMessage}</span>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest hidden md:block">Vegas Protocol v2.0</div>
                </div>
              )}
            </div>

            <div id="slot-machine" className="relative neon-gradient-border rounded-[2rem] p-2 overflow-hidden h-auto flex flex-col shadow-2xl">

              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50 blur-sm"></div>

              <div className="bg-black rounded-2xl border border-white/10 p-2 mb-3 relative shadow-inner min-h-[80px] flex items-center justify-center">
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 pointer-events-none"></div>
                <div className="w-full">
                  <SlotReels isSpinning={isSpinning} reels={currentReels} heldReels={heldReels} onToggleHold={toggleHold} />
                </div>
              </div>

              {heldReels.some(h => h) && (
                <div className="flex justify-center mb-2 -mt-2 relative z-10">
                  <span className="bg-yellow-500 text-black text-[9px] font-black px-3 py-0.5 rounded-full uppercase tracking-widest shadow-[0_0_15px_rgba(234,179,8,0.5)] animate-pulse">
                    Hold Active
                  </span>
                </div>
              )}

              <div className="min-h-auto">
                <AnimatePresence mode="wait">
                  {showQuestion && currentQuestion ? (
                    <motion.div
                      key="question"
                      id="control-panel"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="bg-[#0f0f15] border border-yellow-500/30 rounded-xl p-3 shadow-2xl h-full flex flex-col justify-center relative overflow-hidden"
                    >
                      {/* --- PROGRESS BAR (NEW) --- */}
                      <div className="absolute top-0 left-0 h-1 bg-slate-800 w-full z-10">
                        <motion.div
                          className={`h-full ${timer < 10 ? 'bg-red-500' : 'bg-emerald-500'}`}
                          initial={{ width: '100%' }}
                          animate={{ width: `${(timer / QUESTION_DURATION) * 100}%` }}
                          transition={{ duration: 1, ease: 'linear' }}
                        />
                      </div>

                      <div className="flex justify-between items-center mb-2 pt-2">
                        <div className="text-[10px] font-black text-yellow-500 uppercase tracking-widest flex items-center gap-2">
                          <span>Bonus Event</span>
                          {/* --- TIMER DISPLAY (NEW) --- */}
                          <div className={`flex items-center gap-1 px-2 py-0.5 rounded ${timer < 10 ? 'bg-red-500/20 text-red-400' : 'bg-slate-800 text-slate-400'}`}>
                            <Clock size={10} /> <span className="font-mono">{timer}s</span>
                          </div>
                        </div>

                        <div className="flex gap-1.5">
                          <button onClick={activateFiftyFifty} disabled={balance < getLifelineCost('fifty') || disabledOptions.length > 0} className="px-2 py-1 bg-slate-800 rounded border border-white/10 hover:bg-slate-700 disabled:opacity-30 flex gap-1 items-center">
                            <span className="text-[9px] font-bold text-white">50/50</span>
                            <span className="text-[8px] text-yellow-400 font-mono">-${getLifelineCost('fifty')}</span>
                          </button>

                          <button onClick={activateHint} disabled={balance < getLifelineCost('hint') || lifelinesActive.hint} className="px-2 py-1 bg-slate-800 rounded border border-white/10 hover:bg-slate-700 disabled:opacity-30 flex gap-1 items-center">
                            <span className="text-[9px] font-bold text-white">HINT</span>
                            <span className="text-[8px] text-yellow-400 font-mono">-${getLifelineCost('hint')}</span>
                          </button>

                          <button onClick={activateDebug} disabled={balance < getLifelineCost('debug') || lifelinesActive.debug} className="px-2 py-1 bg-slate-800 rounded border border-white/10 hover:bg-slate-700 disabled:opacity-30 flex gap-1 items-center">
                            <span className="text-[9px] font-bold text-white">DEBUG</span>
                            <span className="text-[8px] text-yellow-400 font-mono">-${getLifelineCost('debug')}</span>
                          </button>
                        </div>
                      </div>

                      <p className="text-white font-bold text-sm mb-3 leading-tight border-b border-white/5 pb-2">{currentQuestion.question}</p>

                      {lifelinesActive.hint && (
                        <div className="mb-2 text-[10px] text-cyan-300 bg-cyan-950/50 p-1.5 rounded border border-cyan-500/30 flex items-center gap-2">
                          <BookOpen size={12} /> <span>{currentQuestion.explanation}</span>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        {currentQuestion.options.map((opt, i) => (
                          <button
                            key={i}
                            onClick={() => handleOptionClick(opt)}
                            disabled={!!selectedOption || disabledOptions.includes(opt)}
                            className={`px-3 py-2 rounded-lg text-left font-bold text-xs transition-all border 
                                        ${selectedOption === opt
                                ? (opt === currentQuestion.answer
                                  ? 'bg-emerald-500/20 border-emerald-500 text-white shadow-[0_0_10px_rgba(16,185,129,0.3)]'
                                  : 'bg-red-500/20 border-red-500 text-white')
                                : (lifelinesActive.debug && opt === currentQuestion.answer
                                  ? 'bg-emerald-500/10 border-emerald-500 text-emerald-200 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.2)]' // Highlight for DEBUG
                                  : 'bg-slate-800/80 border-white/5 text-slate-300 hover:bg-slate-700 hover:border-white/20')} 
                                        ${disabledOptions.includes(opt) ? 'opacity-20 cursor-not-allowed' : ''}`}
                          >
                            <span className="opacity-40 mr-1.5 font-mono text-[10px]">{String.fromCharCode(65 + i)}.</span> {opt}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="controls"
                      id="control-panel"
                      initial={{ opacity: 1 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="bg-slate-800/50 rounded-2xl p-5 border-t border-white/5"
                    >
                      <ControlPanel
                        balance={balance}
                        betAmount={betAmount}
                        setBetAmount={setBetAmount}
                        difficulty={difficulty}
                        setDifficulty={setDifficulty}
                        handleSpin={handleSpin}
                        isSpinning={isSpinning}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <AnimatePresence>
                {activeEvent === 'boss' && !bossModeEngaged && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-6 text-center backdrop-blur-sm rounded-[2rem]">
                    <Skull className="text-red-500 w-16 h-16 mb-4 animate-bounce" />
                    <h2 className="text-2xl font-black text-white mb-2 uppercase">{bossState.name}</h2>
                    <button onClick={() => setBossModeEngaged(true)} className="mt-4 bg-red-600 hover:bg-red-500 text-white font-black text-base px-6 py-2 rounded-xl shadow-[0_0_20px_rgba(220,38,38,0.5)] flex items-center gap-2">
                      <Sword size={18} /> FIGHT BOSS
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* --- CROSS-GAME LINKS (PREMIUM) --- */}
            {/* --- MARKETING BANNERS (COMPACT) --- */}
            <div className="grid grid-cols-2 gap-4 w-full mt-3 shrink-0">
              {/* BANNER 1: SQL BUREAU */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                onClick={() => window.open('https://sbi.datasenseai.com/', '_blank')}
                className="cursor-pointer relative overflow-hidden rounded-2xl p-[2px] group h-32 shadow-[0_0_15px_rgba(239,68,68,0.3)] hover:shadow-[0_0_30px_rgba(239,68,68,0.6)] transition-shadow duration-300"
              >
                {/* Revolving Neon Border */}
                <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-transparent to-red-500 animate-[spin_4s_linear_infinite] opacity-100" />
                <div className="absolute inset-[1px] bg-[#2a0c0e] rounded-2xl z-0" />

                {/* Main Content */}
                <div className="relative z-10 h-full rounded-2xl overflow-hidden flex flex-col justify-between p-3.5">
                  {/* Background Image with Vibrant Overlay */}
                  <div className="absolute inset-0 z-0">
                    <img src="/slot.png" alt="SQL Bureau" className="w-full h-full object-cover opacity-60 group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-red-950 via-red-900/40 to-transparent mix-blend-hard-light" />
                  </div>

                  {/* Header */}
                  <div className="relative z-10 flex items-center gap-2">
                    <div className="bg-red-500/20 p-1.5 rounded-lg border border-red-500/40 backdrop-blur-sm shadow-[0_0_10px_rgba(220,38,38,0.4)]">
                      <ShieldCheck size={16} className="text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-xs font-black text-white italic tracking-wider uppercase drop-shadow-md leading-none">SQL Bureau of Investigation</h3>
                      <div className="text-[8px] font-bold text-red-200/80 uppercase tracking-widest mt-0.5">Top Secret</div>
                    </div>
                  </div>

                  {/* Stats / Pills */}
                  <div className="relative z-10">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-red-600/40 text-white border border-red-500/30 backdrop-blur-md flex items-center gap-1">
                        <ListTodo size={8} /> 100+ Missions
                      </span>
                      <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-red-600/40 text-white border border-red-500/30 backdrop-blur-md flex items-center gap-1">
                        <Star size={8} /> Badges
                      </span>
                    </div>

                    <div className="flex justify-between items-end border-t border-white/10 pt-2">
                      <div className="text-[9px] font-bold text-red-100 flex items-center gap-1">
                        <Clock size={10} className="text-red-400" /> 3h Playtime
                      </div>

                      <div className="h-7 w-7 rounded-full bg-gradient-to-br from-red-500 to-red-700 border border-white/20 flex items-center justify-center text-white shadow-[0_0_15px_rgba(220,38,38,0.6)] group-hover:scale-110 transition-transform">
                        <Play fill="currentColor" size={10} className="ml-0.5" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* BANNER 2: BATTLEGROUND */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                onClick={() => window.open('https://battleground.datasenseai.com/', '_blank')}
                className="cursor-pointer relative overflow-hidden rounded-2xl p-[2px] group h-32 shadow-[0_0_15px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] transition-shadow duration-300"
              >
                {/* Revolving Neon Border */}
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-transparent to-blue-500 animate-[spin_4s_linear_infinite] opacity-100" />
                <div className="absolute inset-[1px] bg-[#081824] rounded-2xl z-0" />

                {/* Main Content */}
                <div className="relative z-10 h-full rounded-2xl overflow-hidden flex flex-col justify-between p-3.5">
                  {/* Background Image with Vibrant Overlay */}
                  <div className="absolute inset-0 z-0">
                    <img src="/battle.png" alt="Battleground" className="w-full h-full object-cover opacity-60 group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-cyan-950 via-cyan-900/40 to-transparent mix-blend-hard-light" />
                  </div>

                  {/* Header */}
                  <div className="relative z-10 flex items-center gap-2">
                    <div className="bg-cyan-500/20 p-1.5 rounded-lg border border-cyan-500/40 backdrop-blur-sm shadow-[0_0_10px_rgba(6,182,212,0.4)]">
                      <Target size={16} className="text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-xs font-black text-white italic tracking-wider uppercase drop-shadow-md leading-none">Battleground</h3>
                      <div className="text-[8px] font-bold text-cyan-200/80 uppercase tracking-widest mt-0.5">Live Arena</div>
                    </div>
                  </div>

                  {/* Stats / Pills */}
                  <div className="relative z-10">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-cyan-600/40 text-white border border-cyan-500/30 backdrop-blur-md flex items-center gap-1">
                        <Zap size={8} /> Battle Royale
                      </span>
                      <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-cyan-600/40 text-white border border-cyan-500/30 backdrop-blur-md flex items-center gap-1">
                        <User size={8} /> 12k Online
                      </span>
                    </div>

                    <div className="flex justify-between items-end border-t border-white/10 pt-2">
                      <div className="text-[9px] font-bold text-cyan-100 flex items-center gap-1">
                        <Gamepad2 size={10} className="text-cyan-400" /> Rank: Gold II
                      </div>

                      <div className="h-7 w-7 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 border border-white/20 flex items-center justify-center text-white shadow-[0_0_15px_rgba(6,182,212,0.6)] group-hover:scale-110 transition-transform">
                        <Play fill="currentColor" size={10} className="ml-0.5" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

          </motion.div>

          {/* RIGHT COLUMN: QUESTS & FLIPS */}
          <div id="quest-panel" className={`lg:col-span-3 flex-col gap-3 lg:h-full lg:overflow-y-auto scrollbar-hide ${mobileTab === 'QUESTS' ? 'flex' : 'hidden lg:flex'}`}>

            <div className="neon-gradient-border rounded-3xl p-5 shadow-lg flex flex-col max-h-[37vh]">
              <h3 className="text-sm font-black text-yellow-400 uppercase tracking-widest mb-4 flex items-center gap-2 shrink-0">
                <Zap size={16} /> Active Bounties
              </h3>

              <div className="space-y-3 overflow-y-auto scrollbar-hide flex-1">
                {activeChallenge && (
                  <div className="bg-gradient-to-r from-orange-900/40 to-slate-900 border border-orange-500/30 rounded-xl p-3 shrink-0">
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-[10px] font-black text-orange-400 uppercase">Daily Task</span>
                      {dailyStatus.completed && !dailyStatus.claimed && (
                        <button onClick={handleDailyClaim} className="text-[10px] font-bold bg-orange-500 text-black px-2 py-0.5 rounded animate-pulse">CLAIM</button>
                      )}
                    </div>
                    <div className="text-xs font-bold text-white">{activeChallenge.title}</div>
                    <div className="text-[10px] font-mono text-slate-400 mt-1">{activeChallenge.reward}</div>
                  </div>
                )}

                {MISSIONS.slice(0, 4).map(m => {
                  const isDone = completedMissions.includes(m.id);
                  const isClaimed = claimedMissions.includes(m.id);
                  return (
                    <div key={m.id} className="bg-white/5 border border-white/5 rounded-xl p-3 flex justify-between items-center shrink-0">
                      <div className="overflow-hidden">
                        <div className={`text-xs font-bold truncate ${isClaimed ? 'text-slate-600 line-through' : 'text-slate-200'}`}>{m.title}</div>
                        <div className="text-[10px] text-slate-500 truncate">{m.reward}</div>
                      </div>
                      {isClaimed ? <CheckCircle size={14} className="text-slate-700" /> : isDone ? (
                        <button onClick={() => handleMissionClaim(m.id)} className="text-[10px] font-bold bg-emerald-500 text-black px-2 py-1 rounded shadow-[0_0_10px_rgba(16,185,129,0.4)]">CLAIM</button>
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-slate-700"></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="neon-gradient-border rounded-3xl p-5 shadow-lg flex flex-col max-h-[35vh]">
              <div className="flex justify-between items-center mb-3 shrink-0">
                <h3 className="text-sm font-black text-fuchsia-400 uppercase tracking-widest flex items-center gap-2"><BookOpen size={16} /> Flash Cards</h3>
                <span className="text-[10px] font-bold bg-fuchsia-500/20 text-white px-2 py-1 rounded border border-fuchsia-500/30">{flippedCards?.length}/4</span>
              </div>

              <div className="flex gap-1 mb-3 shrink-0">
                {['SQL', 'Python', 'AI/ML'].map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`flex-1 py-1.5 rounded text-[10px] font-bold transition-all border ${activeCategory === cat ? 'bg-fuchsia-600 border-fuchsia-400 text-white' : 'bg-black/30 border-white/10 text-slate-400'}`}
                  >
                    {cat === 'AI/ML' ? 'AI' : cat.substring(0, 3)}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2 overflow-y-auto scrollbar-hide pr-1">
                {currentMiniCards.slice(0, 4).map(card => {
                  const isFlipped = flippedCards.includes(card.id);
                  return (
                    <div
                      key={card.id}
                      onClick={() => handleFlip(card.id)}
                      className="h-24 cursor-pointer perspective-1000 group"
                    >
                      <motion.div
                        initial={false}
                        animate={{ rotateY: isFlipped ? 180 : 0 }}
                        transition={{ duration: 0.6 }}
                        className="relative w-full h-full"
                        style={{ transformStyle: 'preserve-3d' }}
                      >
                        <div
                          className="absolute inset-0 bg-gradient-to-br from-slate-800 to-black border border-white/10 rounded-xl p-2 flex flex-col items-center justify-center text-center group-hover:border-fuchsia-500/40 transition-colors"
                          style={{ backfaceVisibility: 'hidden' }}
                        >
                          <div className="text-[8px] font-bold text-fuchsia-500 uppercase tracking-widest mb-0.5">REWARD</div>
                          <div className="text-xs font-bold text-white leading-tight truncate w-full px-1">{card.term}</div>
                          <div className="mt-1 text-[9px] bg-white/10 px-2 py-0.5 rounded text-slate-300 font-mono">$100</div>
                        </div>

                        <div
                          className="absolute inset-0 bg-emerald-950 border border-emerald-500/30 rounded-xl p-2 flex flex-col items-center justify-center text-center"
                          style={{
                            transform: 'rotateY(180deg)',
                            backfaceVisibility: 'hidden'
                          }}
                        >
                          <div className="text-[8px] text-emerald-100 font-medium leading-tight overflow-hidden line-clamp-4">{card.definition}</div>
                          <CheckCircle size={10} className="text-emerald-400 mt-1" />
                        </div>
                      </motion.div>
                    </div>
                  )
                })}
              </div>
            </div>

          </div>

        </div>
      </div>

      {/* MOBILE BOTTOM NAV */}
      <div className="fixed bottom-0 left-0 w-full bg-slate-950/90 backdrop-blur-xl border-t border-white/10 lg:hidden z-50 flex justify-around items-center py-3 pb-6 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
        <button
          onClick={() => setMobileTab('STATS')}
          className={`flex flex-col items-center gap-1 transition-colors ${mobileTab === 'STATS' ? 'text-emerald-400' : 'text-slate-500'}`}
        >
          <LayoutDashboard size={20} />
          <span className="text-[10px] font-bold tracking-widest">STATS</span>
        </button>

        <button
          onClick={() => setMobileTab('GAME')}
          className={`flex flex-col items-center gap-1 -mt-8 bg-slate-900 p-4 rounded-full border border-yellow-500/50 shadow-[0_0_20px_rgba(234,179,8,0.3)] transition-transform active:scale-95 ${mobileTab === 'GAME' ? 'text-yellow-400' : 'text-slate-400'}`}
        >
          <Gamepad2 size={28} className={mobileTab === 'GAME' ? 'drop-shadow-[0_0_10px_rgba(234,179,8,0.8)]' : ''} />
        </button>

        <button
          onClick={() => setMobileTab('QUESTS')}
          className={`flex flex-col items-center gap-1 transition-colors ${mobileTab === 'QUESTS' ? 'text-fuchsia-400' : 'text-slate-500'}`}
        >
          <ListTodo size={20} />
          <span className="text-[10px] font-bold tracking-widest">QUESTS</span>
        </button>
      </div>

      {/* MODALS */}
      <AnimatePresence>
        {showHardware && (
          <HardwareShop
            balance={balance}
            inventory={inventory}
            equipped={equipped}
            onBuy={handleBuyHardware}
            onEquip={handleEquipHardware}
            onClose={() => setShowHardware(false)}
            gems={gems}
            discount={activeStats?.discount || 0}
          />
        )}
        {showShop && <GemShop onClose={() => setShowShop(false)} />}
      </AnimatePresence>

      {showTutorial && <TutorialOverlay onClose={() => setShowTutorial(false)} />}

    </div>
  );
}
